/**
 * Back-office shell. One layout for the whole product — the brand asks for
 * continuity ("repeat the same vocabulary, navigation and visual rules"), so
 * there are no per-tenant layout presets here.
 */
import { Navigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useMe } from "@/hooks/useAuth";
import { useFilteredAdminNav } from "@/hooks/useEnabledNav";
import { api, unwrap } from "@/lib/api";
import { PortalShell } from "./PortalShell";
import { ShellSkeleton } from "./ShellSkeleton";
import { ADMIN_SECTIONS } from "./navSections";

export function AdminShell() {
  const { data: me, isPending, isError } = useMe();
  const location = useLocation();
  const qc = useQueryClient();
  const nav = useFilteredAdminNav();

  const prefetch = (to: string) => {
    if (to === "/admin/dashboard") {
      qc.prefetchQuery({
        queryKey: ["dashboard-summary"],
        queryFn: async () => unwrap((await api.get("/admin/dashboard/summary")).data),
        staleTime: 30_000,
      });
    }
  };

  if (isPending) return <ShellSkeleton items={12} />;
  if (isError || !me) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  // Admins always in. A manager granted an add-on admin group (fullAccess or any
  // permission) can also open the back office; a plain manager goes to their RM
  // portal; anyone else is bounced out.
  const managerHasAdminAccess = me.role === "manager" && (me.fullAccess || (me.permissions?.length ?? 0) > 0);
  if (me.role !== "admin" && !managerHasAdminAccess) {
    return <Navigate to={me.role === "manager" ? "/manager/dashboard" : "/login"} replace />;
  }

  return (
    <PortalShell
      nav={nav}
      sections={ADMIN_SECTIONS}
      prefetch={prefetch}
      supportTo="/admin/support"
      collapsible
    />
  );
}
